import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import useApiErrorHandler from '../useApiErrorHandler';
import useLoading from '../useLoading';
import useModals from '../useModals';
import useConfirmModal from '../useConfirmModal';
import BackupService from '../../services/settings/BackupService';
import {
  BackupConfigurationUpdateDTO,
} from '../../types/settings/backup';
import {
  setConfiguration,
  setHistory,
  setPageSize,
  setRunning,
  setSubmitting,
  startLoading,
  updateFormField,
} from '../../store/slices/settings/backupSlice';

export const useBackup = () => {
  const dispatch: AppDispatch = useDispatch();
  const { handle } = useApiErrorHandler();
  const { showLoadingIndicator, hideLoadingIndicator } = useLoading();
  const { openModal } = useModals();
  const confirm = useConfirmModal();

  const { form, history, paginator, pageSize, isLoading, isSubmitting, isRunning } =
    useSelector((state: RootState) => state.backup);

  const fetchConfiguration = async () => {
    try {
      dispatch(startLoading());
      showLoadingIndicator();

      const response = await BackupService.getConfiguration();

      if (response?.statusCode === 200) {
        dispatch(setConfiguration(response.data));
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
    }
  };

  const fetchHistory = async (page = 0, size = pageSize) => {
    try {
      dispatch(startLoading());

      const response = await BackupService.getHistory(page, size);
      dispatch(setHistory(response));
    } catch (error: any) {
      handle(error);
    }
  };

  const handlerSaveConfiguration = async () => {
    try {
      dispatch(setSubmitting(true));
      showLoadingIndicator();

      const payload: BackupConfigurationUpdateDTO = {
        enabled: form.enabled,
        frequency: form.frequency,
        executionTime: form.executionTime,
        retentionDays: Number(form.retentionDays),
        backupDirectory: form.backupDirectory,
        compressEnabled: form.compressEnabled,
        maxFileCount: Number(form.maxFileCount),
      };

      const response = await BackupService.updateConfiguration(payload);

      if (response?.statusCode === 200) {
        dispatch(setConfiguration(response.data));
        openModal(response.type, {
          title: response.title,
          message: response.message,
        });
      }
    } catch (error: any) {
      handle(error);
    } finally {
      hideLoadingIndicator();
      dispatch(setSubmitting(false));
    }
  };

  const handlerRunBackup = async () => {
    confirm({
      title: 'Ejecutar respaldo',
      message: 'Se iniciará una copia de seguridad manual de la base de datos.',
      icon: 'info',
      confirmText: 'Ejecutar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          dispatch(setRunning(true));
          showLoadingIndicator();

          const response = await BackupService.runBackup();

          if (response?.statusCode === 200 || response?.statusCode === 201) {
            openModal(response.type, {
              title: response.title,
              message: response.message,
            });
            await fetchConfiguration();
            await fetchHistory(0, pageSize);
          }
        } catch (error: any) {
          handle(error);
        } finally {
          hideLoadingIndicator();
          dispatch(setRunning(false));
        }
      },
    });
  };

  const updateField = useCallback(
    <K extends keyof BackupConfigurationUpdateDTO>(
      field: K,
      value: BackupConfigurationUpdateDTO[K]
    ) => {
      dispatch(updateFormField({ field, value }));
    },
    [dispatch]
  );

  const updatePageSize = (size: number) => {
    dispatch(setPageSize(size));
    fetchHistory(0, size);
  };

  return {
    form,
    history,
    paginator,
    pageSize,
    isLoading,
    isSubmitting,
    isRunning,
    fetchConfiguration,
    fetchHistory,
    handlerSaveConfiguration,
    handlerRunBackup,
    updateField,
    updatePageSize,
  };
};

export default useBackup;
